/* ============================================================
   KioskPage — Dedicated staff clock-in/out kiosk + live board.

   Owner ask Feb 2026: the kiosk page should ALSO show "when
   everyone started and finished. Past shifts as well. Make it
   proper info page page."

   Layout (mobile-first):
     • Branded header — "Kiosk · Staff Clock-In · Clock-Out"
     • Live clock (HH:MM:SS, venue local)
     • Clock-in widget (PIN + selfie + action buttons)
     • "On Duty Now" — staff currently clocked in (clock_in time)
     • "Already Clocked Out Today" — completed shifts today with
       start/end + total hours
     • "Past 7 Days" — collapsible per-day rollup with each staff
       member's start/end + day total
     • Back-to-serving link

   Polling: refetches the board every 30 s so a kiosk left open on
   the tablet keeps the on-duty list fresh without nuking the WIP
   PIN entry below.

   Auth: PUBLIC. Backend `/staff/clock/kiosk-action` validates
   PIN + face match server-side; the board endpoint exposes only
   name + role + timestamps (no PII beyond what the printed BOH
   schedule already shows).
   ============================================================ */
import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { HandPlatter, ArrowLeft, Users, History, Clock as ClockIcon, Coffee, Loader2 } from 'lucide-react';
import KioskClockWidget from '../components/KioskClockWidget';
import api from '../lib/api';

// Format an ISO timestamp -> "HH:MM" in the venue's local time.
// We deliberately reuse the browser's local timezone here — the
// kiosk tablet IS at the venue, so the device clock matches the
// venue clock by definition.
function fmtTime(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso.slice(11, 16);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
  } catch {
    return '—';
  }
}

function fmtDate(yyyy_mm_dd) {
  if (!yyyy_mm_dd) return '';
  try {
    const d = new Date(`${yyyy_mm_dd}T12:00:00`);
    return d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
  } catch {
    return yyyy_mm_dd;
  }
}

function fmtDuration(hours) {
  if (hours == null || Number.isNaN(Number(hours))) return '—';
  const h = Math.floor(Number(hours));
  const m = Math.round((Number(hours) - h) * 60);
  if (h === 0 && m === 0) return '0m';
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

function LiveClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="text-center" data-testid="kiosk-live-clock">
      <p className="font-mono text-5xl text-gold tracking-[0.08em] tabular-nums">
        {now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}
      </p>
      <p className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-sans mt-1">
        {now.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
      </p>
    </div>
  );
}

function StaffRow({ row }) {
  const role = (row.user_role || '').replace(/_/g, ' ');
  const ended = !!row.clock_out;
  return (
    <div
      className="flex items-center justify-between px-3 py-2.5 border-b border-white/[0.04] last:border-b-0 hover:bg-white/[0.02]"
      data-testid={`kiosk-row-${row.user_id || row.id}`}
    >
      <div className="min-w-0 flex-1">
        <p className="text-sm font-sans text-white truncate">{row.user_name}</p>
        {role && (
          <p className="text-[10px] uppercase tracking-[0.12em] text-zinc-500 font-sans">{role}</p>
        )}
      </div>
      <div className="text-right shrink-0 ml-3">
        <p className="font-mono text-sm text-zinc-200 tabular-nums">
          {fmtTime(row.clock_in)}
          <span className="text-zinc-600 mx-1.5">→</span>
          <span className={ended ? 'text-zinc-200' : 'text-emerald-300'}>
            {ended ? fmtTime(row.clock_out) : 'on duty'}
          </span>
        </p>
        {ended ? (
          <p className="text-[10px] font-sans text-zinc-500 mt-0.5">{fmtDuration(row.total_hours)}</p>
        ) : row.on_break ? (
          <p className="text-[10px] font-sans text-blue-accent mt-0.5 inline-flex items-center gap-1">
            <Coffee className="w-3 h-3" /> on break
          </p>
        ) : null}
      </div>
    </div>
  );
}

function BoardSection({ title, icon: Icon, accent, rows, emptyHint, testid }) {
  if (!rows || rows.length === 0) {
    if (!emptyHint) return null;
    return (
      <div className="space-y-2" data-testid={testid}>
        <div className="flex items-center justify-between">
          <h3 className="font-serif text-base text-white flex items-center gap-2">
            <Icon className={`w-4 h-4 ${accent}`} />
            {title}
          </h3>
        </div>
        <p className="text-[11px] font-sans text-zinc-600 italic px-1">{emptyHint}</p>
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="flex items-center justify-between">
        <h3 className="font-serif text-base text-white flex items-center gap-2">
          <Icon className={`w-4 h-4 ${accent}`} />
          {title}
        </h3>
        <span className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-sans">
          {rows.length} {rows.length === 1 ? 'person' : 'people'}
        </span>
      </div>
      <div className="bg-white/[0.02] border border-white/5 rounded-sm overflow-hidden">
        {rows.map((r) => <StaffRow key={r.id || `${r.user_id}-${r.clock_in}`} row={r} />)}
      </div>
    </div>
  );
}

function PastDay({ day }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white/[0.02] border border-white/5 rounded-sm" data-testid={`past-day-${day.date}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-white/[0.03]"
      >
        <div className="flex items-center gap-3">
          <span className="text-sm font-sans text-white">{fmtDate(day.date)}</span>
          <span className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-sans">
            {day.rows.length} shift{day.rows.length === 1 ? '' : 's'}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[11px] font-mono text-zinc-400 tabular-nums">
            {fmtDuration(day.total_hours)}
          </span>
          <span className="text-zinc-500 text-xs">{open ? '−' : '+'}</span>
        </div>
      </button>
      {open && (
        <div className="border-t border-white/5">
          {day.rows.map((r) => <StaffRow key={r.id || `${r.user_id}-${r.clock_in}`} row={r} />)}
        </div>
      )}
    </div>
  );
}

export default function KioskPage() {
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const fetchBoard = useCallback(async () => {
    try {
      const { data } = await api.get('/staff/kiosk/board', { params: { days: 7 } });
      setBoard(data);
      setErr(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not load roster board');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBoard();
    const t = setInterval(fetchBoard, 30000);
    return () => clearInterval(t);
  }, [fetchBoard]);

  return (
    <div className="min-h-screen bg-bg-primary text-white" data-testid="kiosk-page">
      <div className="mx-auto max-w-3xl px-4 py-6 sm:py-10 space-y-8">
        {/* Header */}
        <div className="text-center space-y-1.5">
          <div className="inline-flex items-center justify-center gap-2">
            <HandPlatter className="w-5 h-5 text-emerald-300" />
            <h1 className="font-serif text-3xl text-white">Kiosk</h1>
          </div>
          <p className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-sans">
            Staff Clock-In · Clock-Out · Today&apos;s Roster
          </p>
        </div>

        <LiveClock />

        {/* Clock-in widget — unchanged */}
        <div className="max-w-md mx-auto w-full">
          <KioskClockWidget />
          <p className="text-center text-[11px] text-zinc-500 font-sans mt-3">
            Walk up · enter your 4-digit PIN · take a selfie · tap an action.
          </p>
        </div>

        {/* Live board */}
        {loading && (
          <div className="flex justify-center py-8" data-testid="kiosk-board-loading">
            <Loader2 className="w-5 h-5 text-zinc-500 animate-spin" />
          </div>
        )}
        {err && (
          <p className="text-center text-xs font-sans text-rose-300" data-testid="kiosk-board-error">{err}</p>
        )}
        {!loading && board && (
          <div className="space-y-6">
            <BoardSection
              title="On Duty Now"
              icon={Users}
              accent="text-emerald-300"
              rows={board?.today?.on_duty || []}
              emptyHint="No one is clocked in right now."
              testid="board-on-duty"
            />
            <BoardSection
              title="Already Finished Today"
              icon={ClockIcon}
              accent="text-zinc-400"
              rows={board?.today?.completed || []}
              emptyHint={null}
              testid="board-today-completed"
            />

            <div className="space-y-2" data-testid="board-past-7">
              <div className="flex items-center justify-between">
                <h3 className="font-serif text-base text-white flex items-center gap-2">
                  <History className="w-4 h-4 text-amber-200/80" />
                  Past {board?.past?.range_days || 7} Days
                </h3>
                <span className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-sans">
                  Tap a day to expand
                </span>
              </div>
              {(board?.past?.days || []).length === 0 ? (
                <p className="text-[11px] font-sans text-zinc-600 italic px-1">No shifts recorded in this window yet.</p>
              ) : (
                <div className="space-y-2">
                  {(board.past.days || []).map((d) => <PastDay key={d.date} day={d} />)}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="text-center pt-6 border-t border-white/5">
          <Link
            to="/serving"
            className="inline-flex items-center gap-1.5 text-xs font-sans uppercase tracking-[0.14em] text-zinc-500 hover:text-zinc-300"
            data-testid="kiosk-back-to-serving"
          >
            <ArrowLeft className="w-3 h-3" />
            Back to Serving Panel
          </Link>
        </div>
      </div>
    </div>
  );
}
